import { memo, useCallback, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormikProvider, useFormik } from "formik";
import Dialog from "components/DialogV2";
import DialogHeader from "components/DialogV2/DialogHeader";
import DialogTitle from "components/DialogV2/DialogTitle";
import DialogClose from "components/DialogV2/DialogClose";
import DialogContentForm from "components/DialogV2/DialogContentForm";
import DialogActions from "components/DialogV2/DialogActions";
import Button from "components/Button";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import FormDatePicker from "components/Inputs/Form/FormDatePicker";
import FormNumberInput from "components/Inputs/Form/FormNumberInput";
import datetime from "config/datetime";
import ExerciseHelpers from "helpers/ExerciseHelpers";
import useInfoMessage from "hooks/useInfoMessage";
import usePreferences from "hooks/usePreferences";
import useUnitParser from "hooks/useUnitParser";
import useUnitNotation from "hooks/useUnitNotation";
import useOneRepMaxCreateManyMutation from "hooks/useOneRepMaxCreateManyMutation";
import { Variants } from "types";
import type { Exercise, ExerciseMetric } from "types";
import { BOTH_SIDES, generateAddOneRepMaxSchema } from "./AddOneRepMaxDialog.schema";
import type { AddOneRepMaxFormValues } from "./AddOneRepMaxDialog.schema";
import AddOneRepMaxSideField from "./components/AddOneRepMaxSideField";
import useNumberFormatter from "hooks/useNumberFormatter";

interface AddOneRepMaxDialogProps {
  open: boolean;
  onClose: () => void;
  athleteId: string;
  exercise: Exercise | null;
  weightMetric: ExerciseMetric;
  /** The latest 1RM, in the weight metric's storage unit. */
  currentOneRepMax?: number;
}

function getInitialValues(hasVariants: boolean): AddOneRepMaxFormValues {
  return { createdDate: datetime().toISOString(), side: hasVariants ? Variants.Left : null, weight: "" };
}

function AddOneRepMaxDialog(props: AddOneRepMaxDialogProps) {
  const { open, onClose, athleteId, exercise, weightMetric, currentOneRepMax } = props;
  const { t } = useTranslation();
  const formId = useId();
  const [{ imperialSystemEnabled }] = usePreferences();
  const { unitNotation } = useUnitNotation();
  const parseUnit = useUnitParser();
  const revertUnit = useUnitParser({ revert: true });
  const errorMessage = useInfoMessage({ type: "error" });
  const successMessage = useInfoMessage({ type: "success" });
  const { trigger: createOneRepMaxes } = useOneRepMaxCreateManyMutation();
  const { format } = useNumberFormatter();

  const hasVariants = ExerciseHelpers.hasVariants(exercise ?? undefined);
  const weightUnit = unitNotation(weightMetric);
  const currentOneRepMaxDisplay = useMemo(
    () => (currentOneRepMax == null ? null : parseUnit(currentOneRepMax, weightMetric)),
    [currentOneRepMax, parseUnit, weightMetric]
  );

  const weightSlotProps = useMemo(
    () => ({ input: { endAdornment: <Typography sx={{ ml: 1 }}>{weightUnit}</Typography> } }),
    [weightUnit]
  );

  const validationSchema = useMemo(
    () => generateAddOneRepMaxSchema({ imperialSystemEnabled, hasVariants }),
    [hasVariants, imperialSystemEnabled]
  );

  const initialValues = useMemo(() => getInitialValues(hasVariants), [hasVariants]);

  const handleSubmit = useCallback(
    async (values: AddOneRepMaxFormValues) => {
      if (!exercise) return;

      let variants: (Variants | null)[] = [null];

      if (hasVariants && values.side) {
        variants = values.side === BOTH_SIDES ? [Variants.Left, Variants.Right] : [values.side];
      }

      const weight = revertUnit(Number(values.weight), weightMetric).value;
      const oneRepMaxes = variants.map((variant) => ({
        exerciseId: exercise.id,
        weight,
        variant,
        createdDate: values.createdDate,
      }));

      try {
        await createOneRepMaxes([{ athleteId, oneRepMaxes }]);
        onClose();
        successMessage({ message: t("info.one_rep_max_added") });
      } catch {
        errorMessage({ message: t("charting.one_rep_max_add_dialog_error") });
      }
    },
    [
      athleteId,
      createOneRepMaxes,
      errorMessage,
      exercise,
      hasVariants,
      onClose,
      revertUnit,
      successMessage,
      t,
      weightMetric,
    ]
  );

  const formik = useFormik({ initialValues, validationSchema, onSubmit: handleSubmit, enableReinitialize: true });
  const { isSubmitting } = formik;

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} width={400}>
      <DialogHeader>
        <DialogTitle>{t("charting.one_rep_max_add_dialog_title")}</DialogTitle>
        <DialogClose disabled={isSubmitting} />
      </DialogHeader>
      <FormikProvider value={formik}>
        <DialogContentForm id={formId} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Div sx={{ display: "flex", flexDirection: "column", gap: 0.5, textAlign: "center" }}>
            <Typography fontWeight="fontWeightMedium" data-sensitive>
              {t("charting.one_rep_max_add_dialog_message", { exercise: exercise?.name })}
            </Typography>
            {currentOneRepMaxDisplay ? (
              <Typography variant="TextS">
                {t("charting.one_rep_max_add_dialog_current", { value: currentOneRepMaxDisplay.formatted })}
              </Typography>
            ) : null}
          </Div>
          <FormDatePicker
            name="createdDate"
            label={t("charting.one_rep_max_add_dialog_entry_date_label")}
            format="D MMMM YYYY"
            disableFuture
          />
          {hasVariants ? <AddOneRepMaxSideField /> : null}
          <FormNumberInput
            name="weight"
            label={t("global.one_rep_max_abbr_label")}
            decimalScale={2}
            placeholder={currentOneRepMaxDisplay ? format(currentOneRepMaxDisplay.value) : undefined}
            slotProps={weightSlotProps}
          />
        </DialogContentForm>
        <DialogActions sx={{ flexDirection: "column", gap: 2 }}>
          <Button type="submit" form={formId} size="small" fullWidth loading={isSubmitting} disabled={isSubmitting}>
            {t("global.add_button_text")}
          </Button>
          <Button variant="tertiary" size="small" fullWidth onClick={handleClose} disabled={isSubmitting}>
            {t("global.cancel_button_text")}
          </Button>
        </DialogActions>
      </FormikProvider>
    </Dialog>
  );
}

export default memo(AddOneRepMaxDialog);
